<template>
  <!--
    Line Items Configuration — single modal for understanding + editing the
    active column preset. Preview tab: selected preset, column list (name /
    type / formula), sample table. Edit tab: Manage Columns editor (slotted).
  -->
  <b-modal
    id="line-items-config-modal"
    :visible="visible"
    size="xl"
    centered
    scrollable
    modal-class="line-items-config-modal"
    header-class="licm-modal-header"
    body-class="licm-modal-body"
    footer-class="licm-modal-footer"
    @change="$emit('update:visible', $event)"
    @show="onModalShow"
    @shown="syncTabFromProp"
    @hide="$emit('hide', $event)"
  >
    <template #modal-title>
      <div class="licm-title-block">
        <span class="licm-title">Line Items Configuration</span>
        <b-badge variant="light-primary" class="licm-preset-badge">
          {{ presetLabel }}
        </b-badge>
      </div>
    </template>

    <div class="licm-preset-bar mb-2">
      <div class="licm-preset-bar-inner">
        <div class="licm-preset-bar-label">
          <feather-icon icon="LayoutIcon" size="14" class="text-primary mr-50" />
          <span class="small font-weight-bold text-uppercase text-muted">Selected preset</span>
        </div>
        <div class="d-flex align-items-center flex-wrap licm-preset-controls">
        <b-form-select
          :value="presetValue"
          :options="presetOptions"
          size="sm"
          class="licm-preset-select"
          :disabled="presetLoading"
          @change="$emit('preset-change', $event)"
        />
        <b-spinner v-if="presetLoading" small class="ml-1" />
        <small class="text-muted ml-2 licm-preset-hint">
          {{ columnCount }} column{{ columnCount === 1 ? '' : 's' }}
          <span v-if="computedCount"> · {{ computedCount }} computed</span>
        </small>
        </div>
      </div>
    </div>

    <slot name="table-selector" />

    <!-- Do NOT hardcode `active` on a b-tab — it fights v-model and always
         forced Preview even when Columns/Edit opened the modal. -->
    <b-tabs v-model="localTab" pills nav-class="licm-tabs mb-2" content-class="licm-tab-content">
      <b-tab title="Preview">
        <p class="text-muted small mb-2 licm-tab-intro">
          Sample layout for <strong>{{ presetLabel }}</strong> — column roles on the left,
          preview values on the right. This table is not editable; add real invoice rows with
          <strong>Add Item</strong> on the Line Items step.
        </p>

        <b-row>
          <b-col lg="5" class="mb-3 mb-lg-0">
            <div class="licm-section-label mb-2">Columns in this preset</div>
            <div class="licm-column-list">
              <div
                v-for="(col, idx) in previewColumns"
                :key="(col.key || 'col') + '-' + idx"
                class="licm-column-card"
              >
                <div class="licm-column-card-top">
                  <span class="licm-col-index">{{ idx + 1 }}</span>
                  <div class="licm-col-meta">
                    <span class="licm-col-name">{{ col.label }}</span>
                    <code class="licm-col-key">{{ col.key }}</code>
                  </div>
                  <div class="licm-col-badges">
                    <b-badge :variant="typeBadgeVariant(col)" class="licm-type-badge">
                      {{ typeLabel(col) }}
                    </b-badge>
                    <b-badge v-if="col.required" variant="light-danger" class="licm-req-badge">Req</b-badge>
                    <b-badge v-if="col.visible === false" variant="light-secondary" class="licm-req-badge">Hidden</b-badge>
                  </div>
                </div>
                <div v-if="col.formula || isForcedComputed(col) || isComputedColumn(col)" class="licm-col-formula">
                  <span class="licm-fx">ƒ</span>
                  <code v-if="displayFormulaText(col.formula)">{{ displayFormulaText(col.formula) }}</code>
                  <span v-else class="text-muted">Computed (edit formula in Edit Columns)</span>
                </div>
              </div>
              <div v-if="!previewColumns.length" class="text-muted small text-center py-3">
                No columns configured yet.
              </div>
            </div>
          </b-col>

          <b-col lg="7">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="licm-section-label mb-0">Selected preview</div>
              <b-badge :variant="usingLiveData ? 'light-success' : 'light-secondary'" class="licm-data-source-badge">
                {{ usingLiveData ? 'Your line items' : 'Sample data' }}
              </b-badge>
            </div>
            <div class="licm-preview-table-wrap">
              <b-table
                :key="'licm-' + previewFieldsKey"
                :items="sampleRows"
                :fields="previewTableFields"
                small
                bordered
                hover
                responsive
                head-variant="light"
                class="mb-0 licm-preview-table"
                show-empty
                empty-text="No preview rows yet."
              >
                <template #cell()="{ item, field }">
                  <span :class="cellClass(field.key)">{{ formatCell(item, field.key) }}</span>
                </template>
              </b-table>
            </div>
            <small class="text-muted d-block mt-2">
              Read-only sample preview (not your invoice rows). Enter real line items via
              <strong>Add Item</strong> on the Line Items step, or switch to
              <a href="#" class="text-primary" @click.prevent="localTab = 1">Edit Columns</a>
              to add/rename fields and formulas for this invoice.
            </small>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab title="Edit Columns">
        <slot name="edit" />
      </b-tab>
    </b-tabs>

    <template #modal-footer="{ hide }">
      <b-button
        v-if="localTab === 0"
        variant="outline-primary"
        size="sm"
        class="mr-auto"
        @click="localTab = 1"
      >
        <feather-icon icon="SettingsIcon" size="14" class="mr-50" />Edit Columns
      </b-button>
      <b-button
        v-else
        variant="outline-secondary"
        size="sm"
        class="mr-auto"
        @click="localTab = 0"
      >
        <feather-icon icon="EyeIcon" size="14" class="mr-50" />Back to Preview
      </b-button>
      <b-button variant="primary" size="sm" @click="hide()">Done</b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BTabs, BTab, BBadge, BButton, BFormSelect, BTable, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency-format'
import { isComputedColumn } from '@/utils/invoice-calculation-engine'
import { getColumnTypeLabel } from '@/utils/invoice-column-types'
import { displayFormula } from '@/utils/invoice-create-columns'

export default {
  name: 'LineItemsConfigModal',
  components: {
    BModal, BTabs, BTab, BBadge, BButton, BFormSelect, BTable, BRow, BCol, BSpinner,
  },
  props: {
    visible: { type: Boolean, default: false },
    /** 0 = Preview, 1 = Edit Columns */
    initialTab: { type: Number, default: 0 },
    presetValue: { type: [String, Number], default: '' },
    presetOptions: { type: Array, default: () => [] },
    presetLabel: { type: String, default: 'Standard' },
    presetLoading: { type: Boolean, default: false },
    previewColumns: { type: Array, default: () => [] },
    sampleRows: { type: Array, default: () => [] },
    usingLiveData: { type: Boolean, default: false },
    showDecimals: { type: Boolean, default: true },
    /** Optional: keys treated as forced-computed (headcount engine) */
    forcedComputedKeys: { type: Array, default: () => [] },
  },
  data() {
    return { localTab: 0 }
  },
  computed: {
    columnCount() {
      return this.previewColumns.length
    },
    computedCount() {
      return this.previewColumns.filter(
        (c) => isComputedColumn(c) || this.isForcedComputed(c) || !!c.formula,
      ).length
    },
    /** Sample table shows only Visible columns (Edit Columns can hide others). */
    visiblePreviewColumns() {
      return (this.previewColumns || []).filter((c) => c && c.visible !== false)
    },
    previewTableFields() {
      return this.visiblePreviewColumns.map((c) => ({
        key: c.key,
        label: String((c && c.label) || '').trim() || (c && c.key) || '',
        thClass: 'text-nowrap licm-th',
        tdClass: 'text-nowrap',
      }))
    },
    /** Remount sample thead when Edit Columns renames/reorders headers. */
    previewFieldsKey() {
      return this.previewTableFields
        .map((f, i) => `${i}:${f.key}:${f.label}`)
        .join('|')
    },
  },
  watch: {
    visible(v) {
      if (v) this.syncTabFromProp()
    },
    initialTab() {
      if (this.visible) this.syncTabFromProp()
    },
  },
  methods: {
    /** Map parent tab index; treat anything other than 1 as Preview (0). */
    syncTabFromProp() {
      this.localTab = Number(this.initialTab) === 1 ? 1 : 0
    },
    onModalShow() {
      this.syncTabFromProp()
      this.$emit('show')
    },
    isForcedComputed(col) {
      return this.forcedComputedKeys.includes(col.key)
    },
    isComputedColumn,
    displayFormulaText(formula) {
      return displayFormula(formula)
    },
    typeLabel(col) {
      if (this.isForcedComputed(col) && !isComputedColumn(col)) return 'Computed'
      return getColumnTypeLabel(col && col.type) || (col && col.type) || ''
    },
    typeBadgeVariant(col) {
      const t = this.isForcedComputed(col) || isComputedColumn(col) ? 'computed' : col.type
      if (t === 'computed') return 'light-info'
      if (t === 'currency' || t === 'percentage') return 'light-success'
      if (t === 'number') return 'light-warning'
      if (t === 'sac-select') return 'light-primary'
      return 'light-secondary'
    },
    formatCell(item, key) {
      const col = this.previewColumns.find((c) => c.key === key) || {}
      const val = item ? item[key] : undefined
      if (val == null || val === '') return '—'
      if (col.type === 'currency' || col.type === 'computed') {
        const n = parseFloat(val)
        if (!Number.isNaN(n) && (col.type === 'currency' || col.formula || this.isForcedComputed(col) || /amount|rate|ctc|price/i.test(key))) {
          return formatCurrencyUtil(n, this.showDecimals ? 2 : 0)
        }
      }
      if (col.type === 'percentage') {
        const n = parseFloat(val)
        return Number.isNaN(n) ? String(val) : `${n}%`
      }
      return String(val)
    },
    cellClass(key) {
      const col = this.previewColumns.find((c) => c.key === key) || {}
      if (col.type === 'currency' || col.type === 'computed' || col.type === 'number' || col.type === 'percentage') {
        return 'text-right font-weight-medium'
      }
      return ''
    },
  },
}
</script>

<style scoped>
.licm-title-block {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.licm-title {
  font-weight: 600;
  font-size: 1.05rem;
}
.licm-preset-badge {
  font-weight: 600;
  letter-spacing: 0.01em;
}
.licm-preset-bar {
  background: linear-gradient(135deg, #f4fbfa 0%, #f7f7f9 100%);
  border: 1px solid #e3efed;
  border-radius: 0.4rem;
  padding: 0.45rem 0.65rem;
}
.licm-preset-bar-inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
}
.licm-preset-bar-label {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  flex-shrink: 0;
}
.licm-preset-controls {
  gap: 0.35rem;
}
.licm-preset-select {
  min-width: 180px;
  max-width: 260px;
  height: 28px !important;
  min-height: 28px !important;
  padding-top: 0.2rem !important;
  padding-bottom: 0.2rem !important;
  font-size: 0.8rem !important;
}
.licm-preset-hint {
  font-size: 0.75rem;
}
.licm-section-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6e6b7b;
}
.licm-column-list {
  padding-right: 0.1rem;
}
.licm-column-card {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.35rem;
  padding: 0.4rem 0.55rem;
  margin-bottom: 0.35rem;
}
.licm-column-card:last-child {
  margin-bottom: 0;
}
.licm-column-card-top {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
}
.licm-col-index {
  flex-shrink: 0;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  background: #eefaf8;
  color: #00AC99;
  font-size: 0.68rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.1rem;
}
.licm-col-meta {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.licm-col-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: #5e5873;
  line-height: 1.25;
}
.licm-col-key {
  font-size: 0.65rem;
  color: #b9b7c3;
  background: #f4f4f6;
  padding: 0 0.25rem;
  border-radius: 0.2rem;
  width: fit-content;
  margin-top: 0.15rem;
}
.licm-col-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  flex-shrink: 0;
}
.licm-type-badge,
.licm-req-badge {
  font-size: 0.65rem;
  font-weight: 600;
}
.licm-col-formula {
  margin-top: 0.4rem;
  padding-top: 0.35rem;
  border-top: 1px dashed #ebe9f1;
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  font-size: 0.72rem;
}
.licm-fx {
  flex-shrink: 0;
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 0.2rem;
  background: #e8f4ff;
  color: #2c6fbb;
  font-weight: 700;
  font-size: 0.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.licm-col-formula code {
  font-size: 0.72rem;
  color: #5e5873;
  word-break: break-all;
}
.licm-preview-table-wrap {
  border: 1px solid #ebe9f1;
  border-radius: 0.35rem;
  overflow-x: auto;
  background: #fff;
}
.licm-tab-intro {
  line-height: 1.35;
  font-size: 0.78rem;
}
.licm-preview-table >>> thead th {
  font-size: 0.68rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #6e6b7b;
  background: #f8f8fb !important;
  position: sticky;
  top: 0;
  z-index: 1;
}
.licm-preview-table >>> tbody td {
  font-size: 0.78rem;
  vertical-align: middle;
}
.licm-data-source-badge {
  font-size: 0.68rem;
  font-weight: 600;
}
</style>

<style>
/*
 * Unscoped — Bootstrap-Vue modal teleports to body.
 * Single scroll region: modal-body only (no nested overflow traps).
 * Header/footer stay fixed via scrollable + flex column on modal-content.
 */
.line-items-config-modal .modal-xl {
  max-width: min(95vw, 1600px);
  width: 95vw;
}
.line-items-config-modal .modal-dialog {
  max-height: calc(100vh - 0.75rem);
  margin: 0.375rem auto;
  width: 95vw;
  max-width: min(95vw, 1600px);
}
.line-items-config-modal .modal-content {
  max-height: calc(100vh - 0.75rem);
  min-height: min(90vh, 960px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.line-items-config-modal .licm-modal-header,
.line-items-config-modal .licm-modal-footer {
  flex-shrink: 0;
}
.line-items-config-modal .licm-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
.line-items-config-modal .licm-tabs {
  position: sticky;
  top: -0.75rem;
  z-index: 4;
  background: #fff;
  margin-left: -1rem;
  margin-right: -1rem;
  padding: 0.75rem 1rem 0.35rem;
  border-bottom: 1px solid #ebe9f1;
}
.line-items-config-modal .nav-pills .nav-link {
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
}
.line-items-config-modal .nav-pills .nav-link.active {
  background-color: #00AC99;
}
.line-items-config-modal .licm-tab-content {
  padding-top: 0.35rem;
}
@media (max-width: 767px) {
  .line-items-config-modal .modal-dialog {
    max-height: calc(100vh - 0.5rem);
    margin: 0.25rem auto;
  }
  .line-items-config-modal .modal-content {
    max-height: calc(100vh - 0.5rem);
  }
  .line-items-config-modal .licm-preset-bar-inner {
    flex-direction: column;
    align-items: flex-start;
  }
  .line-items-config-modal .licm-preset-select {
    max-width: 100%;
    width: 100%;
  }
}
</style>
